import React from 'react'
import TimeGrid from '@components/TimeGrid'
import CustomHeader from '@components/InstructorScheduler/CustomHeader'
import CustomEvent from '@components/InstructorScheduler/CustomEvent'
import { capitalize } from 'lodash'
import { ICalendarEvent, TActions, IPInstructorScheduler, ISInstructorScheduler } from './interface'
import moment from 'moment'
import {
  AvailabilityModal,
  LoadingModal,
  BasicModal,
  WarningModal,
  AvailabilityBulkDeleteModal,
} from '@components/Modal'
import DeleteModal from './DeleteModal'
import { InjectedIntlProps, injectIntl } from 'react-intl'
import { ACTIONS } from '@utils/constants/availability'
import OverwriteModal from './OverwriteModal'
import omitDeep from '@utils/omit-deep'
import { DATE_FORMAT } from '@utils/constants'

type Props = IPInstructorScheduler & InjectedIntlProps

class InstructorScheduler extends React.Component<Props, ISInstructorScheduler> {
  constructor(props: Props) {
    super(props)

    this.state = {
      currentDate: props.Instructor.CurrentDate || new Date(),
      timeRange: {},
      resource: null,
      formValues: null,
      conflicts: [],
      displayDelete: false,
      action: null,
      modalForm: false,
      modalConflict: false,
      modalOverwrite: false,
      modalDelete: false,
      modalBulkDelete: false,
      modalLoading: false,
      modalSuccess: false,
      modalError: false,
    }
  }

  onSaved = (success: boolean, action: TActions) => {
    const { currentDate } = this.state
    const { refetch } = this.props
    const states = {
      timeRange: {},
      resource: null,
      formValues: null,
      conflicts: [],
      modalForm: false,
      modalConflict: false,
      modalOverwrite: false,
      modalDelete: false,
      modalBulkDelete: false,
      modalLoading: false,
    }

    refetch(
      moment(currentDate).startOf('week').subtract(1, 'day').format(DATE_FORMAT.BASIC),
      moment(currentDate).endOf('week').add(1, 'day').format(DATE_FORMAT.BASIC)
    )

    if (success) {
      this.setState({
        ...states,
        action,
        modalSuccess: true,
        modalError: false,
      })
    } else {
      this.setState({
        ...states,
        action: null,
        modalSuccess: false,
        modalError: true,
      })
    }
  }

  closeAllModals = () => {
    this.setState({
      timeRange: {},
      resource: null,
      formValues: null,
      conflicts: [],
      displayDelete: false,
      action: null,
      modalForm: false,
      modalConflict: false,
      modalOverwrite: false,
      modalDelete: false,
      modalBulkDelete: false,
      modalLoading: false,
      modalSuccess: false,
      modalError: false,
    })
  }

  render() {
    const { loading, intl, AvailabilityCalendar, Instructor, isAdmin = false, refetch } = this.props
    const {
      currentDate,
      timeRange,
      resource,
      action,
      formValues,
      modalForm,
      modalConflict,
      modalOverwrite,
      modalDelete,
      modalBulkDelete,
      modalLoading,
      modalSuccess,
      modalError,
    } = this.state

    return (
      <>
        <TimeGrid
          loading={loading}
          events={AvailabilityCalendar?.getCalendarEvents(currentDate) || []}
          refetch={() => {}}
          customEvent={(event) => (
            <CustomEvent
              {...event}
              Instructor={Instructor}
              isAdmin={isAdmin}
              onDelete={(resource) => {
                this.setState({
                  resource,
                  modalForm: false,
                  displayDelete: true,
                })
              }}
            />
          )}
          customHeader={() => <CustomHeader title={isAdmin ? Instructor.FullName : ''} />}
          onSelectEvent={({ start, resource }: ICalendarEvent) => {
            if (Instructor.canCreateAvailability(start) && AvailabilityCalendar.isEditable(resource)) {
              this.setState(
                {
                  resource,
                },
                () =>
                  this.setState({
                    modalForm: !this.state.displayDelete,
                    modalDelete: this.state.displayDelete,
                  })
              )
            }
          }}
          onSelectSlot={({ start, end }: ICalendarEvent) => {
            if (Instructor.canCreateAvailability(start) && !AvailabilityCalendar.isPastDate(start)) {
              this.setState(
                {
                  timeRange: { start: moment(start), end: moment(end) },
                  resource: null,
                },
                () =>
                  this.setState({
                    modalForm: true,
                  })
              )
            }
          }}
          onNavigate={(currentDate: Date) => {
            const date = moment(currentDate)
            this.setState({ currentDate })
            console.log('onNavigate (InstructorScheduler) currentDate:', { currentDate, date })
            refetch(date.clone().startOf('week').format('YYYY-MM-DD'), date.clone().endOf('week').format('YYYY-MM-DD'))
          }}
          isAdmin={isAdmin}
          timezone={Instructor.TimeZone}
          onBulkDeleteClick={() => {
            this.setState({
              resource: null,
              modalBulkDelete: true,
            })
          }}
        />

        <AvailabilityModal
          open={modalForm}
          onClose={() =>
            this.setState({
              timeRange: {},
              resource: null,
              formValues: null,
              conflicts: [],
              displayDelete: false,
              modalForm: false,
            })
          }
          hasConflicts={(formValues, isTimeOff) => {
            const modalConflict = AvailabilityCalendar.hasConflicts(isTimeOff || false, formValues)

            if (modalConflict) {
              this.setState({
                formValues,
                modalForm: false,
                modalLoading: false,
                modalConflict,
              })
            }

            return modalConflict
          }}
          isOverwritable={(formValues, isTimeOff) => {
            const modalOverwrite = AvailabilityCalendar.hasOverwritables(formValues)

            if (modalOverwrite) {
              let conflicts = {}

              if (!isTimeOff) {
                conflicts = {
                  conflicts: AvailabilityCalendar.getAvailabilityConflicts(formValues),
                }
              }

              this.setState({
                ...conflicts,
                formValues,
                modalForm: false,
                modalLoading: false,
                modalOverwrite,
              })
            }

            return modalOverwrite
          }}
          getUnAvailabilityPayload={(values) => AvailabilityCalendar.getUnAvailabilityPayload(values)}
          getAvailabilityPayload={(values) => AvailabilityCalendar.getAvailabilityPayload(values)}
          onSaving={() => this.setState({ modalForm: false, action: ACTIONS.SAVING, modalLoading: true })}
          onSaved={(success) => this.onSaved(success, ACTIONS.SAVED)}
          Instructor={Instructor}
          resource={resource}
          selectedTimeRange={timeRange}
          isAdmin={isAdmin}
          refetch={refetch}
        />

        <OverwriteModal
          open={modalConflict || modalOverwrite}
          onClose={this.closeAllModals}
          hasConflicts={modalConflict}
          isOverwritable={modalOverwrite}
          payload={{
            ...AvailabilityCalendar?.getAvailabilityPayload(formValues),
            Availabilities: this.state.conflicts.map(({ Availability }) => ({
              InstructorAvailabilityInfo: {
                ...omitDeep(Availability, '__typename'),
              },
            })),
          }}
          getPayload={() => ({
            ...AvailabilityCalendar?.getAvailabilityPayload(formValues),
            Availabilities: AvailabilityCalendar?.getAvailabilityConflicts(formValues)?.map(({ Availability }) => ({
              InstructorAvailabilityInfo: {
                ...omitDeep(Availability, '__typename'),
              },
            })),
          })}
          onSaving={() =>
            this.setState({ modalConflict: false, modalOverwrite: false, action: ACTIONS.SAVING, modalLoading: true })
          }
          onSaved={(success) => this.onSaved(success, ACTIONS.SAVED)}
        />

        <DeleteModal
          open={modalDelete}
          onClose={() =>
            this.setState({
              timeRange: {},
              resource: null,
              formValues: null,
              conflicts: [],
              modalDelete: false,
              displayDelete: false,
              action: null,
            })
          }
          resource={resource}
          AvailabilityCalendar={AvailabilityCalendar}
          onDeleting={() => this.setState({ modalDelete: false, action: ACTIONS.DELETING, modalLoading: true })}
          onDeleted={(success) => this.onSaved(success, ACTIONS.DELETED)}
        />

        <AvailabilityBulkDeleteModal
          open={modalBulkDelete}
          onClose={this.closeAllModals}
          currentDate={moment(Instructor.CurrentDate)}
          date={moment(Instructor.CurrentDate)}
          getPayload={(values, isTimeOff) =>
            isTimeOff
              ? AvailabilityCalendar.getBulkDeleteUnavailabilityPayload(values)
              : AvailabilityCalendar.getBulkDeleteEvents(values) || []
          }
          onDeleting={() => this.setState({ modalBulkDelete: false, action: ACTIONS.DELETING, modalLoading: true })}
          onDeleted={(success) => this.onSaved(success, ACTIONS.DELETED)}
          isAdmin={isAdmin}
          instructorInfo={Instructor.Profile}
        />

        <LoadingModal
          open={modalLoading}
          onClose={() => {}}
          title={`${intl.formatMessage({
            id: `${capitalize(action as string)} in progress`,
          })}...`}
          subtitle={`${intl.formatMessage({ id: 'Please wait a moment' })}.`}
        />

        <BasicModal
          open={modalSuccess}
          onClose={this.closeAllModals}
          type="success"
          modalTitle={intl.formatMessage({
            id: `Your schedule has been ${action}`,
          })}
        />

        <WarningModal open={modalError} onClose={this.closeAllModals} anApiError noButtons />
      </>
    )
  }
}

export default injectIntl(InstructorScheduler)
